import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
} from '@/components/ui/select'
import {
  $fanCategories,
  createFanCategoryFx,
  getFanCategoriesFx,
  updateFanOriginFx,
} from '@/dialogs/EditFanOriginDialog/models.ts'
import {
  $fanId,
  $fanOrigin,
  updateFanOriginId,
} from '@/models/chattingUI/chats/chats.ts'
import type { FanOriginCategory } from '@/modules/chatting/types'

import type { DialogProps } from '../types'
import { CategoryListItem } from './CategoryListItem'
import { CreateCategory } from './CreateCategory'
import { getCategoryName } from './utils.ts'

interface Props extends DialogProps {}

export const EditFanOriginDialog = ({ isOpen, onClose }: Props) => {
  const savedOriginId = useUnit($fanOrigin)
  const isCategoriesLoading = useUnit(getFanCategoriesFx.pending)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [selectValue, setSelectValue] = useState(savedOriginId!)
  const categories = useUnit($fanCategories)
  const isCreating = useUnit(createFanCategoryFx.pending)
  const isSetting = useUnit(updateFanOriginFx.pending)
  const fanId = useUnit($fanId)

  const handleSave = useCallback(async () => {
    await updateFanOriginFx(selectValue)
    updateFanOriginId({ fanId, originId: selectValue })
    onClose()
  }, [selectValue, fanId, onClose])

  const handleSelectChange = useCallback(
    (id: FanOriginCategory['id'] | string) => {
      setSelectValue(Number(id))
      setIsSelectOpen(false)
    },
    [],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Fan Origin</Dialog.Title>
        </Dialog.Header>
        <div className="space-y-1.5">
          <label className="text-text-default text-sm font-medium">
            Category
          </label>
          <Select
            onValueChange={handleSelectChange}
            value={String(selectValue)}
            open={isCreating || isSelectOpen}
            onOpenChange={setIsSelectOpen}
            disabled={isCategoriesLoading}
          >
            <SelectTrigger className="text-secondary-500 w-full text-base outline-none">
              {getCategoryName(selectValue, categories) ?? 'Select Category'}
            </SelectTrigger>
            <SelectContent className="max-h-[250px]">
              <SelectGroup>
                {categories.map((category) => (
                  <CategoryListItem
                    key={category.id + category.name}
                    category={category}
                    isSelected={selectValue === category.id}
                    onSelect={handleSelectChange}
                  />
                ))}
                <CreateCategory />
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button
            type="submit"
            disabled={!selectValue || selectValue === savedOriginId}
            isLoading={isSetting}
            onClick={handleSave}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
